import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AvikaLogo } from '../AvikaLogo';

interface NavItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  roles?: string[];
}

const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: '📊', path: '/app/dashboard' },
    ],
  },
  {
    title: 'Organisation',
    items: [
      { id: 'onboarding', label: 'Org Profile', icon: '🏢', path: '/app/organisation' },
    ],
  },
  {
    title: 'Assessment',
    items: [
      { id: 'assessment', label: 'POSH Assessment', icon: '📋', path: '/app/assessment' },
      { id: 'evidence', label: 'Evidence Manager', icon: '📁', path: '/app/evidence' },
    ],
  },
  {
    title: 'Insights',
    items: [
      { id: 'maturity', label: 'Maturity Report', icon: '📈', path: '/app/maturity' },
      { id: 'reports', label: 'PDF Reports', icon: '📄', path: '/app/reports' },
    ],
  },
  {
    title: 'Administration',
    items: [
      { id: 'admin', label: 'Checklist Admin', icon: '⚙️', path: '/app/admin', roles: ['super_admin', 'org_admin'] },
      { id: 'users', label: 'User Management', icon: '👥', path: '/app/users', roles: ['super_admin', 'org_admin'] },
    ],
  },
];

export const AppShell: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path: string) => location.pathname.startsWith(path);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const roleLabel: Record<string, string> = {
    super_admin: 'Super Admin',
    org_admin: 'Org Admin',
    assessor: 'Assessor',
    icc_member: 'ICC Member',
    viewer: 'Viewer',
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`flex flex-col bg-white border-r border-gray-200 transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100">
          <AvikaLogo height={collapsed ? 30 : 42} variant="dark" markOnly={collapsed} />
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="ml-auto text-gray-400 hover:text-gray-600 text-sm"
          >
            {collapsed ? '→' : '←'}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
          {NAV_GROUPS.map((group) => {
            const visibleItems = group.items.filter(
              (item) => !item.roles || (user && item.roles.includes(user.role))
            );
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.title}>
                {!collapsed && (
                  <p className="px-2 mb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    {group.title}
                  </p>
                )}
                {visibleItems.map((item) => (
                  <Link
                    key={item.id}
                    to={item.path}
                    className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm transition-colors ${
                      isActive(item.path)
                        ? 'bg-amber-50 text-amber-700 font-semibold'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                    title={collapsed ? item.label : ''}
                  >
                    <span className="text-base flex-shrink-0">{item.icon}</span>
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="border-t border-gray-100 p-3">
          {!collapsed ? (
            <div>
              {/* Profile Link */}
              <Link
                to="/app/profile"
                className={`flex items-center gap-2 px-2 py-2 mb-2 rounded-lg text-sm transition-colors ${
                  isActive('/app/profile')
                    ? 'bg-amber-50 text-amber-700 font-semibold'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span>Profile & Security</span>
              </Link>
              
              {/* User Info */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#C4956A] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {user?.full_name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{user?.full_name}</p>
                  <p className="text-xs text-gray-500">{roleLabel[user?.role || ''] || user?.role}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-red-500 text-xs"
                  title="Logout"
                >
                  ↩
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Link
                to="/app/profile"
                className="w-full flex justify-center text-gray-400 hover:text-amber-600"
                title="Profile"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex justify-center text-gray-400 hover:text-red-500"
                title="Logout"
              >
                ↩
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
